// Minimal offline queue using IndexedDB (via localStorage fallback).
// Used to queue habit_log upserts when the user is offline.
//
// Each item: { id, op: 'upsert_log', payload, createdAt }
//   payload includes: habit_id, user_id, log_date, status, value, note?, mood?
//
// NOTE: localStorage is intentional here — this queue holds only non-sensitive
// habit-tracking state (habit IDs, dates, status). Auth tokens live in
// Supabase's own storage. No PII or secrets are persisted in this key.

import { supabase } from './supabase';

const KEY = 'mfh:offline-queue';

const read = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
  catch { return []; }
};
const write = (arr) => localStorage.setItem(KEY, JSON.stringify(arr));

export const queueLength = () => read().length;

export const enqueueLogUpsert = (payload) => {
  const arr = read();
  // De-dup by (habit_id, log_date): replace existing entry
  const idx = arr.findIndex(
    (x) => x.op === 'upsert_log' &&
      x.payload.habit_id === payload.habit_id &&
      x.payload.log_date === payload.log_date
  );
  const item = { id: crypto.randomUUID(), op: 'upsert_log', payload, createdAt: Date.now() };
  if (idx >= 0) arr[idx] = item; else arr.push(item);
  write(arr);
  return item;
};

export const flushQueue = async () => {
  const arr = read();
  if (arr.length === 0) return { ok: 0, fail: 0 };
  let ok = 0, fail = 0;
  const remaining = [];
  for (const item of arr) {
    if (item.op === 'upsert_log') {
      const { data: existing } = await supabase
        .from('habit_logs')
        .select('id')
        .eq('habit_id', item.payload.habit_id)
        .eq('log_date', item.payload.log_date)
        .maybeSingle();
      const { error } = existing
        ? await supabase.from('habit_logs').update(item.payload).eq('id', existing.id)
        : await supabase.from('habit_logs').insert(item.payload);
      if (error) { fail += 1; remaining.push(item); }
      else { ok += 1; }
    }
  }
  write(remaining);
  return { ok, fail };
};

export const onOnline = (cb) => {
  const handler = () => cb();
  window.addEventListener('online', handler);
  return () => window.removeEventListener('online', handler);
};

export const isOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine);
